/* Import components */

import React, {useEffect, useState} from 'react';
import {DataTablePaginated, Form} from '../components';
import {Field} from 'formik';
import {axios} from '../libs/axios';
import ReactPaginate from 'react-paginate';
import {useSelector} from 'react-redux';
import {User} from '../states/actions';
import Select from 'react-select';
import logger from '../utils/logger';
import {
	legalFieldCategoryOptions,
	sortLegalFieldsByCategory,
} from '../constants/legalFieldCategories';

/* Status filter */

const StatusFilter = ({statusFilter, onChange}) => {
	const opts = [
		{label: 'All', value: 'all'},
		{label: 'Active', value: 'active'},
		{label: 'Inactive', value: 'inactive'},
	];
	return (
		<>
			{opts.map(o => {
				const sel = statusFilter === o.value;
				return (
					<div
						key={o.value}
						onClick={() => onChange(o.value)}
						className={`status status-${o.value}${sel ? ' selected' : ''}`}>
						{o.value !== 'all' && (<span className="indicator" />)}
						{o.label}
					</div>
				);
			})}
		</>
	);
};

/* Export legal fields */

export const LegalFields = () => {
	const user = useSelector(User.get());
	const [fields, setFields] = useState([]);
	const [loading, setLoading] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const [totalRecords, setTotalRecords] = useState(0);
	const [sizePerPage, setSizePerPage] = useState(100);
	const [statusFilter, setStatusFilter] = useState('all');
	const [search, setSearch] = useState('');
	const [sortField, setSortField] = useState('category');
	const [sortOrder, setSortOrder] = useState('asc');

	/* Modal state */
	const [showModal, setShowModal] = useState(false);
	const [isEditing, setIsEditing] = useState(false);
	const [editId, setEditId] = useState(null);
	const [initialValues, setInitialValues] = useState({
		field_id: '',
		name_et: '',
		name_en: '',
		name_ru: '',
		short_name_et: '',
		short_name_en: '',
		short_name_ru: '',
		scope_et: '',
		category: '',
		keywords: [],
		is_active: true,
	});

	/* Table columns */
	const tableColumns = [
		{
			dataField: 'field_id',
			text: 'ID',
			sort: true,
			headerClasses: 'cell-field-id',
			classes: 'cell-field-id',
		},
		{
			dataField: 'category',
			text: 'Category',
			sort: true,
			headerClasses: 'cell-category',
			classes: 'cell-category',
		},
		{
			dataField: 'name_et',
			text: 'Name (ET)',
			sort: true,
			headerClasses: 'cell-name',
			classes: 'cell-name',
		},
		{
			dataField: 'short_name_et',
			text: 'Short (ET)',
			sort: true,
			headerClasses: 'cell-short-name',
			classes: 'cell-short-name',
			formatter: (cell, row) => cell || row.short_label || '',
		},
		{
			dataField: 'scope_et',
			text: 'Scope (ET)',
			sort: false,
			headerClasses: 'cell-scope',
			classes: 'cell-scope',
			formatter: (cell) => {
				const text = cell || '';
				if (!text) {
					return '';
				}
				return <span title={text}>{text}</span>;
			},
		},
		{
			dataField: 'keywords',
			text: 'Keywords',
			sort: false,
			headerClasses: 'cell-keywords',
			classes: 'cell-keywords',
			formatter: (cell) => {
				const text = Array.isArray(cell) && cell.length ? cell.join('; ') : '';
				if (!text) {
					return '';
				}
				return <span title={text}>{text}</span>;
			},
		},
		{
			dataField: 'name_en',
			text: 'Name (EN)',
			sort: true,
			headerClasses: 'cell-name',
			classes: 'cell-name',
		},
		{
			dataField: 'name_ru',
			text: 'Name (RU)',
			sort: true,
			headerClasses: 'cell-name',
			classes: 'cell-name',
		},
		{
			dataField: 'is_active',
			text: 'Status',
			sort: true,
			headerClasses: 'cell-status',
			classes: 'cell-status',
			formatter: (cell) => (
				<span className={`badge badge-${cell ? 'success' : 'secondary'}`}>
					{cell ? 'Active' : 'Inactive'}
				</span>
			),
		},
		{
			dataField: 'actions',
			text: 'Actions',
			headerClasses: 'cell-actions',
			classes: 'cell-actions',
			formatter: (cell, row) => (
				<div className="actions">
					<button
						onClick={(e) => {
							e.stopPropagation();
							handleDelete(row);
						}}
						className="button button-small button-red">
						Delete
					</button>
				</div>
			),
		},
	];

	/* Fetch legal fields */
	const sortFields = (items, activeSortField = 'category', activeSortOrder = 'asc') => {
		const sorted = [...items];
		if (activeSortField === 'category') {
			const ordered = sortLegalFieldsByCategory(sorted);
			return activeSortOrder === 'desc' ? ordered.reverse() : ordered;
		}

		const order = activeSortOrder === 'desc' ? -1 : 1;
		sorted.sort((a, b) => {
			const aValue = a[activeSortField];
			const bValue = b[activeSortField];
			if (aValue == null && bValue == null) return 0;
			if (aValue == null) return 1;
			if (bValue == null) return -1;
			if (typeof aValue === 'number' && typeof bValue === 'number') {
				return (aValue - bValue) * order;
			}
			if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
				return (Number(aValue) - Number(bValue)) * order;
			}
			return String(aValue).localeCompare(String(bValue), 'et') * order;
		});
		return sorted;
	};

	const fetchFields = async (page = 1, size = 100, q = '', status = 'all', activeSortField = 'category', activeSortOrder = 'asc') => {
		setLoading(true);
		try {
			const {data, total} = await axios.get('/legal-fields', {
				params: {
					page,
					sizePerPage: size,
					q,
					status,
					sortField: activeSortField,
					sortOrder: activeSortOrder,
				},
			});
			
			// Filter locally based on status
			let filteredData = data || [];
			if (status === 'active') {
				filteredData = filteredData.filter(f => f.is_active);
			} else if (status === 'inactive') {
				filteredData = filteredData.filter(f => !f.is_active);
			}
			
			// Filter locally based on search
			if (q && q.trim()) {
				const searchLower = q.toLowerCase();
				filteredData = filteredData.filter(f =>
					f.name_et?.toLowerCase().includes(searchLower) ||
					f.name_en?.toLowerCase().includes(searchLower) ||
					f.name_ru?.toLowerCase().includes(searchLower) ||
					f.category?.toLowerCase().includes(searchLower) ||
					f.scope_et?.toLowerCase().includes(searchLower) ||
					f.short_name_et?.toLowerCase().includes(searchLower) ||
					f.short_name_en?.toLowerCase().includes(searchLower) ||
					f.short_name_ru?.toLowerCase().includes(searchLower) ||
					(Array.isArray(f.keywords) ? f.keywords.join('; ') : '').toLowerCase().includes(searchLower) ||
					String(f.field_id).includes(searchLower)
				);
			}

			filteredData = sortFields(filteredData, activeSortField, activeSortOrder);
			setFields(filteredData);
			setTotalRecords(filteredData.length);
		} finally {
			setLoading(false);
		}
	};

	/* Initial fetch */
	useEffect(() => {
		fetchFields(currentPage, sizePerPage, search, statusFilter, sortField, sortOrder);
	}, [currentPage, sizePerPage, search, statusFilter, sortField, sortOrder]);

	/* Handle table sorting */
	const handleTableChange = (type, {sortField, sortOrder}) => {
		if (type === 'sort') {
			setSortField(sortField);
			setSortOrder(sortOrder);
			setCurrentPage(1);
		}
	};

	/* CRUD handlers */
	const openNew = () => {
		setInitialValues({
			field_id: '',
			name_et: '',
			name_en: '',
			name_ru: '',
			short_name_et: '',
			short_name_en: '',
			short_name_ru: '',
			scope_et: '',
			category: '',
			keywords: [],
			is_active: true,
		});
		setIsEditing(false);
		setEditId(null);
		setShowModal(true);
	};

	const openEdit = (field) => {
		setInitialValues({
			_id: field._id,
			field_id: field.field_id,
			name_et: field.name_et,
			name_en: field.name_en,
			name_ru: field.name_ru,
			short_name_et: field.short_name_et || field.short_label || '',
			short_name_en: field.short_name_en || '',
			short_name_ru: field.short_name_ru || '',
			scope_et: field.scope_et || '',
			category: field.category || '',
			keywords: field.keywords || [],
			is_active: field.is_active,
		});
		setIsEditing(true);
		setEditId(field._id);
		setShowModal(true);
	};

	const closeModal = () => {
		setShowModal(false);
		setInitialValues({
			field_id: '',
			name_et: '',
			name_en: '',
			name_ru: '',
			short_name_et: '',
			short_name_en: '',
			short_name_ru: '',
			scope_et: '',
			category: '',
			keywords: [],
			is_active: true,
		});
		setIsEditing(false);
		setEditId(null);
	};

	const onSuccess = () => {
		closeModal();
		fetchFields(currentPage, sizePerPage, search, statusFilter, sortField, sortOrder);
	};

	const onError = (error) => {
		logger.error('Form submission error:', error);
	};

	const handleDelete = async (field) => {
		let n; n = window.nConfirm({
			icon: 'fa-question-circle',
			title: 'Delete legal field?',
			text: `<span>${field.name_et}</span>`,
			onYes: async () => {
				try {
					await axios.delete(`/legal-fields/${field._id}`);
					window.nSuccess({
						title: 'Success!',
						text: 'Legal field deleted successfully',
						icon: 'fa-check-square',
					});
					fetchFields(currentPage, sizePerPage, search, statusFilter, sortField, sortOrder);
				} catch (error) {
					window.nError({
						title: 'Error!',
						text: error.message || 'Failed to delete legal field. It may be in use by lawyers.',
						icon: 'fa-exclamation-triangle',
					});
				}
				n?.close();
			},
			onNo: () => { n?.close(); }
		});
	};

	/* Category options */
	const categoryOptions = legalFieldCategoryOptions;

	/* Render */
	return (
		<>
			<div id="legal-fields" className="row table_v2 legal-fields">
				
				<div className="col p-0">
					<div className="x_panel chatbot-builder">
						<div className="header-menu d-flex justify-content-between align-items-center">
							<div className="header-title d-flex align-items-center">
								<h1>Legal Fields</h1>
							</div>
							<div className="header-actions d-flex align-items-center">
								<div className="action action-search">
									<input
										type="text"
										placeholder="Search legal fields…"
										value={search}
										onChange={(e) => {
											setSearch(e.target.value);
											setCurrentPage(1);
										}}
									/>
									<i className="fa-solid fa-magnifying-glass"></i>
								</div>
								<div className="action action-addnew">
									<button className="button button-blue" onClick={openNew}>
										+ Add New
									</button>
								</div>
							</div>
						</div>
						<div className="x_content">
							<div className="page-container d-flex flex-column">
								{loading ? (
									<div>Loading…</div>
								) : (
									<>
										<div className="page-header d-flex justify-content-between align-items-center">
											<div className="filter-group d-flex align-items-center">
												<div className="filter filter-status">
													<StatusFilter
														statusFilter={statusFilter}
														onChange={(status) => {
															setStatusFilter(status);
															setCurrentPage(1);
														}}
													/>
												</div>
											</div>
											<div className="filter filter-pagesize d-flex justify-content-end align-items-center">
												<label htmlFor="pageSize">Items per page:</label>
												<select
													id="pageSize"
													value={sizePerPage}
													onChange={(e) => {
														setSizePerPage(+e.target.value);
														setCurrentPage(1);
													}}>
													{[50, 100, 200, 500].map((n) => (
														<option key={n} value={n}>
															{n}
														</option>
													))}
												</select>
											</div>
										</div>
										<div className="page-content nopaginate">
											<DataTablePaginated
												data={fields}
												columns={tableColumns}
												onTableChange={handleTableChange}
												defaultSorted={[{ dataField: 'category', order: 'asc' }]}
												rowClasses={() => 'table-row'}
												onRowClick={(row, e) => {
													if (e?.target?.closest('.cell-actions')) {
														return;
													}
													openEdit(row);
												}}
											/>
										</div>
										<div className="page-footer d-flex justify-content-between align-items-center">
											<div className="filter filter-pagination d-flex align-items-center">
												{Math.ceil(totalRecords / sizePerPage) > 0 && (
													<ReactPaginate
														containerClassName={'pagination'}
														activeClassName={'active'}
														previousLinkClassName={'fa-regular fa-chevron-left'}
														nextLinkClassName={'fa-regular fa-chevron-right'}
														previousClassName={currentPage === 1 ? 'd-none' : 'previous'}
														nextClassName={
															currentPage === Math.ceil(totalRecords / sizePerPage) ? 'd-none' : 'next'
														}
														previousLabel={'Previous'}
														nextLabel={'Next'}
														breakLabel={'…'}
														pageCount={Math.ceil(totalRecords / sizePerPage)}
														marginPagesDisplayed={2}
														pageRangeDisplayed={5}
														onPageChange={(e) => {
															setCurrentPage(e.selected + 1);
														}}
														forcePage={currentPage - 1}
													/>
												)}
											</div>
											<div className="filter filter-pagesize d-flex justify-content-end align-items-center">
												<label htmlFor="pageSize2">Items per page:</label>
												<select
													id="pageSize2"
													value={sizePerPage}
													onChange={(e) => {
														setSizePerPage(+e.target.value);
														setCurrentPage(1);
													}}>
													{[50, 100, 200, 500].map((n) => (
														<option key={n} value={n}>
															{n}
														</option>
													))}
												</select>
											</div>
										</div>
									</>
								)}
							</div>
						</div>
					</div>
				</div>
			
				{/* Modal */}
				{showModal && (
				<div className="modal">
					<div className="modal-content" onClick={e=>e.stopPropagation()}>
						<div className="modal-header">
							<span className="modalclose" onClick={closeModal}>&times;</span>
							<h2>{isEditing ? 'Edit Legal Field' : 'New Legal Field'}</h2>
						</div>
						<div className="modal-form">
							<Form 
								url="/legal-fields" 
								usePUT={isEditing ? true : false} 
								initialValues={initialValues} 
								onSuccess={onSuccess}
								onError={onError}
							>
								<div className="form-row col-half row-field-id">
									<Field
										as="input"
										type="number"
										id="field_id"
										name="field_id"
										placeholder="Field ID"
										required
										disabled={isEditing}
										className={isEditing ? 'readonly' : ''}
									/>
								</div>
								<div className="form-row col-full row-name-et">
									<Field
										as="input"
										type="text"
										id="name_et"
										name="name_et"
										placeholder="Name (Estonian)"
										required
									/>
								</div>
								<div className="form-row col-full row-short-name-et">
									<Field
										as="input"
										type="text"
										id="short_name_et"
										name="short_name_et"
										placeholder="Short name (Estonian)"
									/>
								</div>
								<div className="form-row col-full row-short-name-en">
									<Field
										as="input"
										type="text"
										id="short_name_en"
										name="short_name_en"
										placeholder="Short name (English)"
									/>
								</div>
								<div className="form-row col-full row-short-name-ru">
									<Field
										as="input"
										type="text"
										id="short_name_ru"
										name="short_name_ru"
										placeholder="Short name (Russian)"
									/>
								</div>
								<div className="form-row col-full row-scope-et">
									<Field
										as="textarea"
										id="scope_et"
										name="scope_et"
										placeholder="Scope (Estonian)"
										rows={3}
									/>
								</div>
								<div className="form-row col-full row-keywords">
									<Field name="keywords">
									{({ field, form }) => (
										<textarea
											id="keywords"
											name={field.name}
											placeholder="Keywords (semicolon-separated)"
											rows={3}
											value={Array.isArray(field.value) ? field.value.join('; ') : ''}
											onChange={(e) => {
												const parsed = e.target.value
													.split(';')
													.map((item) => item.trim())
													.filter(Boolean);
												form.setFieldValue('keywords', parsed);
											}}
										/>
									)}
									</Field>
								</div>
								<div className="form-row col-full row-name-en">
									<Field
										as="input"
										type="text"
										id="name_en"
										name="name_en"
										placeholder="Name (English)"
										required
									/>
								</div>
								<div className="form-row col-full row-name-ru">
									<Field
										as="input"
										type="text"
										id="name_ru"
										name="name_ru"
										placeholder="Name (Russian)"
										required
									/>
								</div>
								<div className="form-row col-full row-category">
									<Field id="category" name="category">
									{({field, form}) => (
										<Select
											id="category"
											name={field.name}
											value={categoryOptions.find((i) => i.value === form.values.category)}
											options={categoryOptions}
											onChange={(option) => {form.setFieldValue(field.name, option.value)}}
										/>
									)}
									</Field>
								</div>
								<div className="form-row col-full row-active">
									<div style={{padding: '8px 0', display: 'flex', alignItems: 'center'}}>
										<label htmlFor="is_active" style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', justifyContent: 'flex-start'}}>
											<Field
												type="checkbox"
												id="is_active"
												name="is_active"
												style={{margin: 0, width: '22px', height: '22px'}}
											/>
											<span style={{fontSize: '13px'}}>Active</span>
										</label>
									</div>
								</div>
								<div className="form-row col-full row-actions">
									<button type="button" className="button button-large" onClick={closeModal}>Cancel</button>
									<button type="submit" className="button button-large button-blue">{isEditing ? 'Update' : 'Create'}</button>
								</div>
							</Form>
						</div>
					</div>
				</div>
				)}
		</div>
		</>
	);
};
